import React, { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import * as model from "@/interfaces/nftTypes";
import NFTCardsWindow from "@/containers/nft/NFTCardsWindow";
import { NFTCardItem, NFTItemClickFunc } from "@/interfaces/nftTypes";
import dynamic from "next/dynamic";
import { Tab, TabList, TabPanel, TabsProps } from "react-tabs";
import bridge from "@/utils/dsBridge";
import Toast from "@/components/Toast";
import { produce } from "immer";
const Tabs = dynamic<TabsProps>(
	import("react-tabs").then((mod) => mod.Tabs),
	{ ssr: false },
);

import NFTCard from "@/components/nft/NFTCard";
import getConfetti from "@/utils/getConfetti";
import { GetServerSideProps } from "next";
import { BridgeCallHandlers, AvatarUIStatus } from "@/interfaces/enums";
import {
	AvatarsResponse,
	useAvatarQuota,
	useFilterAvatarInfinite,
	useMystery,
} from "@/hooks/useRequest/useAvatarRequest";
import {
	avatarLike,
	avatarOwn,
	avatarUnlike,
	getAvatarCategories,
	getAvatarOwner,
	getStarLevelFromServer,
} from "@/service/avatar.services";
import { debounce } from "lodash";
import rfStore from "@/utils/rfStore";
import Head from "next/head";
import RFLayout from "@/layout/RFLayout";
import useWindowSize from "@/hooks/useWindowSize";
import { convertAvatarName2Abbr } from "@/utils/util";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useRouter } from "next/router";
import { CATEGORIES, StarLevelMappingGetting, StarLevelMappingOwn } from "@/interfaces/constants";
import NTFAvatarStarFilter from "@/containers/nft/NTFAvatarStarFilter";
import NTFDeposit from "@/containers/nft/NFTDeposit";
import NFTShare from "@/containers/nft/NFTShare";
import ConfirmModal from "@/containers/common/ConfirmModal";
import CustomerModal from "@/containers/common/CustomerModal";
import { LikeFilledIcon, ShareIcon, UnLikeFilledIcon } from "@/components/RFIcon";
import Button from "@/components/Button";
import EmptyContainerV2 from "@/containers/common/EmptyContainerv2";

enum StartLevelType {
	OWN_AVATAR = "OWN_AVATAR",
	GET_AVATAR = "GET_AVATAR",
}

const getStarLevel = (level: number, type: StartLevelType) => {
	const mapping = type === StartLevelType.GET_AVATAR ? StarLevelMappingGetting : StarLevelMappingOwn;
	return (
		mapping.find((item) => item.level === level) || {
			level: 0,
			price: "",
		}
	);
};

export default function NFTAvatars({
	categories,
	queryTabIndex,
	initSelectTab,
}: {
	categories: model.CategoryTab[];
	queryTabIndex: number;
	initSelectTab: { type: string; value: string };
}) {
	const { t } = useTranslation("nft");
	const router = useRouter();
	const [avatarUIStatus, setAvatarUIStatus] = useState(AvatarUIStatus.ON_STORE);
	const [visible, setVisible] = useState(false);
	const [loading, setLoading] = useState(false);
	const [ownOk, setOwnOk] = useState(false);

	const [selectedImageId, setSelectedImageId] = useState("");
	const [nickname, setNickname] = useState("Rocker");

	const { width, height } = useWindowSize();
	const [modalSize, setModalSize] = useState({ height: 360, width: 300 });
	const [activeCard, setActiveCard] = useState<NFTCardItem>({
		id: "",
		loadUrl: "",
		name: "",
		likedBy: 0,
		owner: "",
		canBeOwned: false,
	});

	const [tabIndex, setTabIndex] = useState(queryTabIndex);
	const [selectTab, setSelectTab] = useState(initSelectTab);

	const [starLevelVisible, setStarLevelVisible] = useState(false);
	const [starLevel, setStarLevel] = useState(-1);
	const [starRanks, setStarRanks] = useState<number[]>([]);

	// 数据请求
	const [filterCategories, setFilterCategories] = useState<model.CategoryTab[]>(categories);
	const { data: mysteryData, mutate: mutateMystery } = useMystery();
	const { data, assets, size, setSize, mutate, hasMore, isLoadingMore } = useFilterAvatarInfinite({
		...selectTab,
		ownerMode: avatarUIStatus === AvatarUIStatus.ON_MYSTERY ? 1 : 0,
		starRanks: showStarFilter() ? starRanks : [],
	});
	const { data: avatarQuota, isLoading: isLoadingQuota } = useAvatarQuota();

	// 分享
	const shareRef = useRef<any>(null);

	useEffect(() => {
		if (!avatarQuota || !avatarQuota.canOwnedTopStarLevel) return;
		if (avatarQuota && avatarQuota.canOwnedTopStarLevel > 0) {
			const _stars = new Array(avatarQuota.canOwnedTopStarLevel).fill(undefined).map((_, index) => index + 1);
			setStarRanks(_stars);
		}
	}, [avatarQuota]);

	const handleLoadMore = () => {
		if (hasMore && !isLoadingMore) {
			setSize(size + 1);
		}
	};

	const handleConfetti = useCallback(() => getConfetti(), []);

	// 头像更换按钮
	const selectAvatarDisabled = () => {
		return selectedImageId === activeCard.id;
	};
	const handleSelect = () => {
		bridge?.call(BridgeCallHandlers.ON_NFT_AVATAR_SELECT, {
			imageId: activeCard?.id,
			imageUrl: activeCard?.loadUrl,
		});
		setVisible(false);
	};

	const ownButtonDisabled = () => {
		const starUnAvailable = starLevel < Number(activeCard.starRank);

		return starUnAvailable || !activeCard.canBeOwned || activeCard.owner !== "--";
	};
	const handleOwn = async () => {
		setLoading(true);
		const isOk = await avatarOwn(activeCard.id as string);
		if (!isOk) {
			setLoading(false);
			return;
		}
		setLoading(false);
		handleConfetti();
		setActiveCard((pre) => {
			return {
				...pre,
				owner: nickname,
				canBeOwned: !pre.canBeOwned,
			};
		});
		mutate(data);
		// 展示OK按钮
		setOwnOk(true);
		// 如果没有选择过头像默认为选择头像d
	};

	const handleOwnOk = () => {
		setVisible(false);
		if (!selectedImageId) {
			handleSelect();
			return;
		}
		setAvatarUIStatus(AvatarUIStatus.ON_STORE);
		mutateMystery();
		setSelectTab({
			type: categories[0].type,
			value: categories[0].value,
		});
		setFilterCategories(categories);
		setTabIndex(0);
	};

	const debouncedToggleLikeClickHandler = useCallback(
		debounce(async () => {
			const isLike = activeCard.isFavorite;

			const isOk = isLike ? await avatarUnlike(activeCard.id as string) : await avatarLike(activeCard.id as string);

			if (isOk) {
				setActiveCard((pre) => {
					return {
						...pre,
						isFavorite: !isLike,
						likedBy: isLike ? pre.likedBy - 1 : pre.likedBy + 1,
					};
				});

				mutate(data);
			}
		}, 300),
		[activeCard],
	);

	// 喜欢||不喜欢
	const handleLikeChange = async (imageId: string, liked: boolean, tabValue: string) => {
		// 乐观更新
		mutate((data: AvatarsResponse[] | undefined) => {
			const newData = produce(data, (draft) => {
				if (!draft) return;
				for (let i = 0; i < draft.length; i++) {
					draft[i].data.forEach((item) => {
						if (item.imageId === imageId) {
							item.desiredMyself = liked;
							liked ? item.desireCounts++ : item.desireCounts--;
						}
					});
				}
			});
			return newData ? [...newData] : undefined;
		}, false);

		const isOk = liked ? await avatarLike(imageId) : await avatarUnlike(imageId);
		// 仅like页面和失败时， 需要再次刷新列表
		if (!isOk || tabValue == CATEGORIES.like.value || visible) {
			mutate(data);
		}
	};

	const debouncedLikeClickHandler = useCallback(
		debounce((coverId: string, liked: boolean, tabValue: string) => handleLikeChange(coverId, liked, tabValue), 300),
		[],
	);

	// card 点击
	const handleItemClick: NFTItemClickFunc = async (itemId, type, liked) => {
		if (ownOk) {
			setOwnOk(false);
		}

		if (type === model.ClickType.NTF_CARD_MYSTERY) {
			setStarLevelVisible(true);
			return;
		}

		const activeCardItem = assets?.find((item) => item.imageId === itemId);
		if (!activeCardItem) {
			return;
		}

		if (type === model.ClickType.NFT_CARD_LIKED_ICON) {
			debouncedLikeClickHandler(activeCardItem.imageId, liked as boolean, selectTab.value);
			return;
		}
		let NFTCardItem: model.NFTCardItem = {
			id: activeCardItem.imageId,
			loadUrl: activeCardItem.imageUrl,
			name: convertAvatarName2Abbr(activeCardItem.name),
			likedBy: activeCardItem.desireCounts,
			canBeOwned: activeCardItem.canBeOwned,
			isFavorite: activeCardItem.desiredMyself,
			starRank: activeCardItem.starRank,
		};

		setVisible(true);
		setActiveCard(NFTCardItem);

		// 等待拉取owner, 可能会有时序问题
		const owner = await getAvatarOwner(itemId);
		setActiveCard({ ...NFTCardItem, owner: owner?.ownerNickname || "--" });

		if (avatarUIStatus === AvatarUIStatus.ON_MYSTERY) {
			showToastMsg({ ...NFTCardItem, owner: owner?.ownerNickname });
		}
	};

	const showToastMsg = (card: model.NFTCardItem) => {
		let msg = "";
		if (!card.canBeOwned) {
			msg = t("cant-not-own-it");
		}
		if (card.owner) {
			msg = t("someone-has-own-it");
		}

		msg && Toast.info(msg);
	};

	// tab
	const handleTabClick = (index: number) => {
		const tabItem = filterCategories[index];
		setSelectTab({
			type: tabItem.type,
			value: encodeURIComponent(tabItem.value),
		});
		mutate(data);
		setTabIndex(index);
	};

	const handleChangeUiStateMystery = () => {
		setAvatarUIStatus(AvatarUIStatus.ON_MYSTERY);
		setTabIndex(1);
		setSelectTab({
			type: categories[2].type,
			value: categories[2].value,
		});
		setFilterCategories(categories.filter((item) => item.value !== CATEGORIES.own.value));
		setStarLevelVisible(false);
	};

	const renderOwnerText = () => {
		if (activeCard.canBeOwned) {
			return t("own-it");
		}
		// TODO: 自己won过, 理论上不会出现，
		if (activeCard.owner && activeCard.owner !== "--") {
			return t("owned-by-others");
		}

		return t("not-available");
	};

	useEffect(() => {
		const modalWidth = width - 38 * 2;
		const modalStyle = {
			width: modalWidth,
			height: modalWidth * 1.283,
		};
		setModalSize(modalStyle);
	}, [height, width]);

	useEffect(() => {
		const clientInfo = rfStore.getRFState();
		if (clientInfo?.nickname) {
			setNickname(clientInfo.nickname);
		}
	}, []);

	useEffect(() => {
		const params = bridge?.call(BridgeCallHandlers.GET_SELECTED_NFT_AVATAR);
		if (!params) {
			return;
		}

		try {
			const result = JSON.parse(params);
			if (result.imageId && result.imageId !== "0") {
				setSelectedImageId(result.imageId);
			}
		} catch (error) {
			console.log(error);
		}
	}, []);

	useEffect(() => {
		getUserStarLevel();
	}, []);

	const getUserStarLevel = async () => {
		try {
			const level = await getStarLevelFromServer();

			setStarLevel(level);
		} catch (error) {
			console.log(error);
		}
	};

	function showStarFilter() {
		/* 除了 owner tab */
		return selectTab.value !== "own" && selectTab.value !== "like";
	}

	const getMysteryData = () => {
		if (selectTab.value !== CATEGORIES.own.value || !mysteryData?.hasQualifications) {
			return [];
		}
		return [
			{
				id: "mystery",
				loadUrl: "https://avatar-cdn.rockflow.tech/avatar-default/mysterybox.png",
				name: t("select-mystery"),
				likedBy: 0,
				isMystery: true,
			},
		];
	};

	const handleDirectToRule = () => {
		router.push("/nft/avatars/rule");
		setStarLevelVisible(false);
		setVisible(false);
	};

	const renderTabPanelContent = (index: number) => {
		let list: Array<NFTCardItem> = Array(12).fill({
			loadUrl: "",
		});

		if (!assets) {
			return <NFTCardsWindow list={list} onItemClick={handleItemClick} />;
		}

		// 神秘掩藏
		const mystery: Array<NFTCardItem> = getMysteryData();

		if (assets.length === 0 && mystery.length === 0) {
			let emptyTitle = "";
			if (selectTab.value === CATEGORIES.own.value) {
				emptyTitle = t("own-tab-empty-avatar");
			} else if (avatarUIStatus === AvatarUIStatus.ON_MYSTERY) {
				emptyTitle = t("like-tab-empty-avatar-mystery");
			} else if (selectTab.value === CATEGORIES.like.value) {
				emptyTitle = t("like-tab-empty-avatar");
			}
			return <EmptyContainerV2 title={t("nothing-here", { ns: "common" })} subTitle={emptyTitle} />;
		}

		list = assets.map((asset) => ({
			id: asset.imageId,
			loadUrl: asset.imageUrl,
			name: convertAvatarName2Abbr(asset.name),
			likedBy: asset.desireCounts,
			isFavorite: asset.desiredMyself,
			starRank: asset.starRank,
			withLikedIcon: filterCategories[index].value !== "own" && avatarUIStatus === AvatarUIStatus.ON_STORE,
			isFrozen: avatarUIStatus === AvatarUIStatus.ON_MYSTERY && !asset.canBeOwned,
		}));

		list.unshift(...mystery);

		return (
			<>
				<NFTCardsWindow list={list} onItemClick={handleItemClick} onScrollEnd={handleLoadMore} />
			</>
		);
	};

	const renderStarLevelRule = () => {
		if (avatarUIStatus === AvatarUIStatus.ON_STORE) {
			if (selectTab.value === "own") {
				const info = getStarLevel(activeCard.starRank as number, StartLevelType.OWN_AVATAR);
				return (
					<div className="text-center text-xs text-primary dark:text-f1ece8">
						<p>{t("modal-description-own", { price: info.price })}</p>
						<div className="text-center font-bold underline underline-offset-2 mt-1">
							<span onClick={handleDirectToRule}>{t("check-NFT-rules")}</span>
						</div>
					</div>
				);
			}
		}

		const info = getStarLevel(activeCard.starRank as number, StartLevelType.GET_AVATAR);
		return (
			<div className="text-center text-xs text-primary dark:text-f1ece8">
				<p>{t("modal-description", { level: info.level, price: info.price })}</p>
				<div className="text-center font-bold underline underline-offset-2 mt-1">
					<span onClick={handleDirectToRule}>{t("check-NFT-rules")}</span>
				</div>
			</div>
		);
	};

	const renderShare = () => {
		return (
			<div
				className="absolute right-4 top-3 bg-dark/[7%] rounded-full flex items-center justify-center w-9 h-9"
				onClick={() => {
					if (!shareRef.current) return;
					shareRef.current
						.handleShare()
						.then(() => {
							setVisible(false);
						})
						.finally(() => {
							setVisible(false);
						});
				}}
			>
				<ShareIcon className="w-5 h-5 text-white" />
			</div>
		);
	};

	const renderModalCardBtn = () => {
		if (avatarUIStatus === AvatarUIStatus.ON_STORE) {
			if (selectTab.value === "own") {
				return renderSelectAsAvatarBtnGroup();
			}
			return renderLikeBtnGroup();
		}

		if (avatarUIStatus === AvatarUIStatus.ON_MYSTERY) {
			if (ownOk) {
				return renderOwnOkBtnGroup();
			}
			return renderMysteryBtnGroup();
		}

		return null;
	};

	const renderSelectAsAvatarBtnGroup = () => {
		return (
			<div
				className="mt-5 mb-16"
				onClick={(e) => {
					if (e.target === e.currentTarget) {
						setVisible(false);
					}
				}}
			>
				<Button block onClick={handleSelect} disabled={selectAvatarDisabled()} loading={loading}>
					{selectAvatarDisabled() ? t("selected-as-avatar") : t("select-as-avatar")}
				</Button>
			</div>
		);
	};

	const renderMysteryBtnGroup = () => {
		return (
			<div
				className="mt-5 mb-16"
				onClick={(e) => {
					if (e.target === e.currentTarget) {
						setVisible(false);
					}
				}}
			>
				<Button block disabled={ownButtonDisabled()} onClick={handleOwn} loading={loading}>
					{renderOwnerText()}
				</Button>
			</div>
		);
	};

	const renderLikeBtnGroup = () => {
		return (
			<div
				className="mt-8 mb-12 text-center"
				onClick={(e) => {
					if (e.target === e.currentTarget) {
						setVisible(false);
					}
				}}
			>
				<div
					className="inline-flex items-center justify-center border-2 rounded-full border-ff38a3 w-14 h-14"
					onClick={debouncedToggleLikeClickHandler}
				>
					{activeCard.isFavorite ? (
						<LikeFilledIcon className="w-6 h-6  text-ff38a3" />
					) : (
						<UnLikeFilledIcon className="w-6 h-6 text-ff38a3" />
					)}
				</div>
			</div>
		);
	};

	const renderOwnOkBtnGroup = () => {
		return (
			<div
				className="mt-5 mb-16"
				onClick={(e) => {
					if (e.target === e.currentTarget) {
						setVisible(false);
					}
				}}
			>
				<Button block onClick={handleOwnOk}>
					{t("ok", { ns: "common" })}
				</Button>
			</div>
		);
	};

	const renderStarModalTitle = () => {
		if (starLevel <= 0) {
			return t("star-level0-title");
		}

		return t("star-level-title", {
			level: getStarLevel(starLevel, StartLevelType.GET_AVATAR).level,
		});
	};

	const renderStarModalDesc = () => {
		if (starLevel <= 0) {
			return t("star-level0-description");
		}
		return t("star-level-description", {
			price: getStarLevel(starLevel, StartLevelType.GET_AVATAR).price,
			level: getStarLevel(starLevel, StartLevelType.GET_AVATAR).level,
		});
	};

	const renderStarModalCancelButton = () => {
		if (starLevel <= 0 || starLevel === 5) {
			return t("check-rules");
		}

		return t("want-level-star", { level: 5 });
	};

	return (
		<>
			<Head>
				<title>{avatarUIStatus === AvatarUIStatus.ON_STORE ? t("nft-avatar") : t("select-your-own-nft-avatar")}</title>
			</Head>

			<div className="h-full overflow-y-hidden">
				<NTFDeposit {...avatarQuota} loading={isLoadingQuota} />

				<Tabs selectedIndex={tabIndex} onSelect={handleTabClick} className="h-full">
					<TabList className="nft_tab__list hidden-scroll-bar">
						{filterCategories.map((category) => (
							<Tab className="nft_tab__item" selectedClassName="nft_tab__item__active" key={category.value}>
								{t(`${category.type}-${category.value}`.toLocaleLowerCase())}
							</Tab>
						))}
					</TabList>

					{showStarFilter() && (
						<NTFAvatarStarFilter
							labelText={t("star-ranking-filter")}
							initial={starRanks}
							onSelected={(stars) => {
								setStarRanks(stars);
							}}
						/>
					)}

					{filterCategories.map((category, index) => (
						<TabPanel key={category.value}>{renderTabPanelContent(index)}</TabPanel>
					))}
				</Tabs>

				<NFTShare ref={shareRef} type={1} imgUrl={activeCard.loadUrl} />

				<CustomerModal visible={visible} onClose={() => setVisible(false)} bgLevel="deep">
					<div
						className="w-[90vw] max-w-sm"
						onClick={(e) => {
							if (e.target === e.currentTarget) {
								setVisible(false);
							}
						}}
					>
						<div style={{ ...modalSize }} className="mx-auto relative">
							<NFTCard
								loadUrl={activeCard.loadUrl}
								name={activeCard.name}
								owner={activeCard.owner}
								likedBy={activeCard.likedBy}
								loadLarge
								starRank={activeCard.starRank as number}
							></NFTCard>
							{renderShare()}
						</div>
						<div className="mx-auto" style={{ width: modalSize.width + "px" }}>
							{renderModalCardBtn()}
							{renderStarLevelRule()}
						</div>
					</div>
				</CustomerModal>

				{/* 星级弹窗 */}
				<ConfirmModal
					visible={starLevelVisible}
					onClose={() => setStarLevelVisible(false)}
					title={renderStarModalTitle()}
					onOkText={t("check-ok")}
					onOk={handleChangeUiStateMystery}
					onCancelText={renderStarModalCancelButton()}
					onCancel={handleDirectToRule}
				>
					<div>{renderStarModalDesc()}</div>
				</ConfirmModal>
			</div>
		</>
	);
}

export const getServerSideProps: GetServerSideProps = async ({ locale = "en", query }) => {
	// 提前渲染 Tabs
	const data = await getAvatarCategories();

	// query上有参数 默认渲染对应tab页
	const findIndex = data.findIndex((item) => query.tab === item.value);
	const queryTabIndex = findIndex > -1 ? findIndex : 0;
	const initSelectTab = {
		type: data[queryTabIndex].type,
		value: data[queryTabIndex].value,
	};

	return {
		props: {
			categories: data,
			queryTabIndex,
			initSelectTab,
			...(await serverSideTranslations(locale, ["error", "common", "nft", "share"])),
		},
	};
};

NFTAvatars.getLayout = function getLayout(page: ReactElement) {
	return <RFLayout>{page}</RFLayout>;
};
